import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'

import { protoBtnProps, type AppBtnTone } from '@/shared/lib/app-button'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/components/ui/button'

import { PageEmptyCard } from './PageCard'
import { PageIconBadge, type PageIconTone } from './PageIconBadge'

type Props = {
  icon: LucideIcon
  tone: PageIconTone
  title: ReactNode
  subtitle?: ReactNode
  children?: ReactNode
  className?: string
}

type ActionVariant = 'primary' | 'secondary'

/** @deprecated 请用 `<Button asChild>`；保留供极少数 className 拼接 */
export function pageEmptyStateActionClass(variant: ActionVariant = 'primary') {
  return cn(
    protoBtnProps({
      variant: variant === 'primary' ? 'primary' : 'secondary',
      size: 'md',
      className: 'min-h-10 px-6',
    }).className,
  )
}

export function PageEmptyStateAction({
  variant = 'primary',
  tone = 'amber',
  className,
  children,
  ...props
}: LinkProps & { variant?: ActionVariant; tone?: AppBtnTone }) {
  return (
    <Button
      asChild
      {...protoBtnProps({
        variant: variant === 'primary' ? 'primary' : 'secondary',
        tone,
        size: 'md',
        className: cn('min-h-10 px-6', className),
      })}
    >
      <Link {...props}>{children}</Link>
    </Button>
  )
}

export function PageEmptyStateActionButton({
  variant = 'primary',
  tone = 'amber',
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ActionVariant; tone?: AppBtnTone }) {
  return (
    <Button
      type="button"
      {...protoBtnProps({ variant, tone, size: 'md', className: cn('min-h-10 px-6', className) })}
      {...props}
    >
      {children}
    </Button>
  )
}

export function PageEmptyState({ icon, tone, title, subtitle, children, className }: Props) {
  return (
    <PageEmptyCard className={cn('flex flex-col items-center justify-center py-16', className)}>
      <PageIconBadge icon={icon} tone={tone} size="lg" />
      <p className="mt-5 text-sm font-medium text-foreground">{title}</p>
      {subtitle ? <p className="mt-1 max-w-md text-xs text-muted-foreground">{subtitle}</p> : null}
      {children ? <div className="mt-5">{children}</div> : null}
    </PageEmptyCard>
  )
}
